export type PillarId = 'seguidores' | 'notificaciones' | 'interaccion' | 'gamificacion' | 'comunidad' | 'monetizacion';

export type ValidationStatus = 'pass' | 'warning' | 'fail';

export interface PillarMetric {
  label: string;
  value: number;
  target: number;
  unit: string;
  status: ValidationStatus;
}

export interface PillarDetail {
  id: PillarId;
  title: string;
  icon: string;
  description: string;
  score: number;
  status: ValidationStatus;
  statusLabel: string;
  weight: number;
  metrics: PillarMetric[];
  highlights: string[];
  gaps: string[];
}

export interface SocialAuditSummary {
  overallScore: number;
  overallStatus: ValidationStatus;
  totalPillars: number;
  passedPillars: number;
  warningPillars: number;
  failedPillars: number;
  lastAudit: string;
  missionStatement: string;
  notMission: string[];
  isMission: string[];
}

export const socialAuditSummary: SocialAuditSummary = {
  overallScore: 84,
  overallStatus: 'pass',
  totalPillars: 6,
  passedPillars: 4,
  warningPillars: 1,
  failedPillars: 1,
  lastAudit: '2026-06-18T14:30:00Z',
  missionStatement: 'RFW es la red social que conecta radios, locutores, programas y oyentes en un solo ecosistema vivo.',
  notMission: ['Directorio de emisoras', 'Plataforma de streaming', 'Panel técnico de administración'],
  isMission: ['Red Social de Radios', 'Red Social de Locutores', 'Red Social de Programas', 'Red Social de Oyentes'],
};

export const pillarDetails: PillarDetail[] = [
  {
    id: 'seguidores',
    title: 'Seguidores',
    icon: 'ri-user-heart-line',
    description: 'Capacidad del ecosistema para conectar radios con oyentes, locutores con fans, y programas con audiencias fidelizadas mediante relaciones de seguimiento bidireccionales.',
    score: 92,
    status: 'pass',
    statusLabel: 'Validado',
    weight: 20,
    metrics: [
      { label: 'Oyentes siguiendo radios', value: 187500, target: 150000, unit: 'oyentes', status: 'pass' },
      { label: 'Fans siguiendo locutores', value: 94300, target: 80000, unit: 'fans', status: 'pass' },
      { label: 'Oyentes siguiendo programas', value: 76200, target: 70000, unit: 'oyentes', status: 'pass' },
      { label: 'Relaciones de seguimiento totales', value: 412000, target: 350000, unit: 'conexiones', status: 'pass' },
      { label: 'Tasa de follow-back', value: 68, target: 60, unit: '%', status: 'pass' },
      { label: 'Radios con +1000 seguidores', value: 34, target: 30, unit: 'radios', status: 'pass' },
    ],
    highlights: [
      '187.5K oyentes siguen al menos una radio activamente',
      'Tasa de follow-back del 68% — ecosistema recíproco',
      '34 radios superan los 1,000 seguidores orgánicos',
    ],
    gaps: [],
  },
  {
    id: 'notificaciones',
    title: 'Notificaciones',
    icon: 'ri-notification-3-line',
    description: 'Sistema de notificaciones que mantiene informada a la comunidad sobre actividad relevante: nuevos programas, locutores al aire, eventos, concursos y menciones.',
    score: 78,
    status: 'pass',
    statusLabel: 'Validado',
    weight: 15,
    metrics: [
      { label: 'Notificaciones enviadas (30d)', value: 2840000, target: 2000000, unit: 'envíos', status: 'pass' },
      { label: 'Tasa de apertura', value: 42, target: 45, unit: '%', status: 'warning' },
      { label: 'Tasa de click-through', value: 18, target: 20, unit: '%', status: 'warning' },
      { label: 'Canales activos', value: 5, target: 5, unit: 'canales', status: 'pass' },
      { label: 'Preferencias personalizadas', value: 73, target: 70, unit: '%', status: 'pass' },
      { label: 'Tiempo medio de entrega', value: 1.2, target: 2, unit: 'seg', status: 'pass' },
    ],
    highlights: [
      '2.84M notificaciones enviadas en 30 días — alta actividad',
      '5 canales activos: push, email, in-app, SMS, webhook',
      '73% de usuarios tienen preferencias personalizadas',
    ],
    gaps: [
      'Tasa de apertura 42% — por debajo del target 45%',
      'CTR 18% — optimizar contenido de notificaciones',
    ],
  },
  {
    id: 'interaccion',
    title: 'Interacción',
    icon: 'ri-chat-3-line',
    description: 'Medición de la actividad social en tiempo real: comentarios en programas, reacciones a publicaciones, compartidos, menciones y tiempo de engagement.',
    score: 88,
    status: 'pass',
    statusLabel: 'Validado',
    weight: 20,
    metrics: [
      { label: 'Comentarios mensuales', value: 156000, target: 120000, unit: 'comentarios', status: 'pass' },
      { label: 'Reacciones mensuales', value: 342000, target: 250000, unit: 'reacciones', status: 'pass' },
      { label: 'Compartidos mensuales', value: 89000, target: 70000, unit: 'shares', status: 'pass' },
      { label: 'Menciones entre usuarios', value: 124000, target: 100000, unit: 'menciones', status: 'pass' },
      { label: 'Tiempo medio de sesión', value: 18.5, target: 15, unit: 'min', status: 'pass' },
      { label: 'Mensajes en cabina (30d)', value: 480000, target: 400000, unit: 'mensajes', status: 'pass' },
    ],
    highlights: [
      '342K reacciones mensuales — señal social fuerte',
      '18.5 min de sesión media — engagement profundo',
      '480K mensajes en cabina — conversación viva',
    ],
    gaps: [],
  },
  {
    id: 'gamificacion',
    title: 'Gamificación',
    icon: 'ri-trophy-line',
    description: 'Sistema de logros, insignias, niveles y recompensas que motiva la participación continua de oyentes, locutores y radios dentro del ecosistema.',
    score: 72,
    status: 'warning',
    statusLabel: 'Atención',
    weight: 15,
    metrics: [
      { label: 'Insignias otorgadas', value: 18400, target: 20000, unit: 'insignias', status: 'warning' },
      { label: 'Oyentes con nivel +5', value: 12400, target: 15000, unit: 'oyentes', status: 'warning' },
      { label: 'Logros desbloqueados', value: 52000, target: 60000, unit: 'logros', status: 'warning' },
      { label: 'Rankings activos', value: 8, target: 8, unit: 'rankings', status: 'pass' },
      { label: 'Tasa de retorno gamificado', value: 58, target: 65, unit: '%', status: 'warning' },
      { label: 'Locutores con insignias', value: 185, target: 200, unit: 'locutores', status: 'pass' },
    ],
    highlights: [
      '8 rankings activos cubriendo radios, locutores y oyentes',
      '185 locutores han ganado al menos una insignia',
      'Sistema de niveles funcionando para +12K oyentes',
    ],
    gaps: [
      'Insignias otorgadas por debajo del target (-8%)',
      'Tasa de retorno gamificado 58% vs target 65%',
      'Faltan 2,600 oyentes para meta de nivel +5',
    ],
  },
  {
    id: 'comunidad',
    title: 'Comunidad',
    icon: 'ri-group-line',
    description: 'Salud del tejido social: grupos de oyentes, comunidades por radio, actividad en foros, eventos comunitarios y creación de contenido generado por usuarios.',
    score: 81,
    status: 'pass',
    statusLabel: 'Validado',
    weight: 20,
    metrics: [
      { label: 'Grupos de oyentes activos', value: 340, target: 300, unit: 'grupos', status: 'pass' },
      { label: 'Posts comunitarios (30d)', value: 28500, target: 25000, unit: 'posts', status: 'pass' },
      { label: 'Eventos comunitarios creados', value: 156, target: 120, unit: 'eventos', status: 'pass' },
      { label: 'Contenido UGC mensual', value: 42000, target: 35000, unit: 'piezas', status: 'pass' },
      { label: 'Participación en concursos', value: 28, target: 25, unit: '%', status: 'pass' },
      { label: 'Radios con comunidad activa', value: 28, target: 25, unit: 'radios', status: 'pass' },
    ],
    highlights: [
      '340 grupos de oyentes activos — tejido social sólido',
      '42K piezas de contenido UGC al mes',
      '28 radios tienen comunidad propia activa',
    ],
    gaps: [],
  },
  {
    id: 'monetizacion',
    title: 'Monetización',
    icon: 'ri-money-dollar-circle-line',
    description: 'Preparación del ecosistema para monetización social: clubes VIP, revenue share, marketplace publicitario, patrocinadores y sistema de pagos.',
    score: 65,
    status: 'fail',
    statusLabel: 'No Validado',
    weight: 10,
    metrics: [
      { label: 'Clubes VIP activos', value: 11, target: 15, unit: 'clubes', status: 'warning' },
      { label: 'Miembros VIP totales', value: 8470, target: 12000, unit: 'miembros', status: 'warning' },
      { label: 'Ingreso potencial mensual', value: 42650, target: 80000, unit: 'USD', status: 'fail' },
      { label: 'Radios con patrocinadores', value: 18, target: 25, unit: 'radios', status: 'warning' },
      { label: 'Anunciantes activos', value: 42, target: 60, unit: 'anunciantes', status: 'fail' },
      { label: 'Revenue Share activo', value: 12, target: 20, unit: 'radios', status: 'warning' },
    ],
    highlights: [
      '11 clubes VIP funcionando con 8,470 miembros',
      '$42,650/mes en ingreso potencial identificado',
      '18 radios ya tienen patrocinadores activos',
    ],
    gaps: [
      'Ingreso potencial $42.6K vs target $80K — brecha del 47%',
      'Solo 12 radios en Revenue Share vs target de 20',
      '42 anunciantes vs target de 60 — atraer más marcas',
      'Miembros VIP 8,470 vs target 12,000 — acelerar adopción',
    ],
  },
];

export const pillarColorConfig: Record<ValidationStatus, { bg: string; badge: string; text: string; icon: string; bar: string; ring: string }> = {
  pass: { bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', text: 'text-emerald-600', icon: 'ri-checkbox-circle-line', bar: 'bg-emerald-500', ring: 'stroke-emerald-500' },
  warning: { bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700 border-amber-200', text: 'text-amber-600', icon: 'ri-error-warning-line', bar: 'bg-amber-500', ring: 'stroke-amber-500' },
  fail: { bg: 'bg-rose-50', badge: 'bg-rose-100 text-rose-700 border-rose-200', text: 'text-rose-600', icon: 'ri-close-circle-line', bar: 'bg-rose-500', ring: 'stroke-rose-500' },
};

export const pillarIcons: Record<PillarId, string> = {
  seguidores: 'ri-user-heart-line',
  notificaciones: 'ri-notification-3-line',
  interaccion: 'ri-chat-3-line',
  gamificacion: 'ri-trophy-line',
  comunidad: 'ri-group-line',
  monetizacion: 'ri-money-dollar-circle-line',
};

export const pillarShortLabels: Record<PillarId, string> = {
  seguidores: 'Seguidores',
  notificaciones: 'Notificaciones',
  interaccion: 'Interacción',
  gamificacion: 'Gamificación',
  comunidad: 'Comunidad',
  monetizacion: 'Monetización',
};